import { types } from "mobx-state-tree";
import fetcher from "../utils/fetcher";
import { cleanFormError } from "../utils/errorMessage";

// tenantchange_signdoc rows (each: EXTFILENAME = the doc, PROVIDED = "Y" signed)
const signDocRows = (data) =>
  Array.isArray(data) ? data : data?.value ?? (data ? [data] : []);

// a row that still needs signing: it has a doc (EXTFILENAME) not yet provided
const isUnsignedRow = (r) => !!r?.EXTFILENAME && r?.PROVIDED !== "Y";

/**
 * Store for the "Choosing Tenants" (בחירת דיירים) feature. Wraps the ERP
 * (Priority) endpoints proxied by the coral backend under /tenants:
 *   - get/add_tenant_apartment_plan  → ZCOC_APARTMENTPLANS_SUBFORM (Plans tab)
 *   - get_tenant_selection_types       → ZCOC_TENANTSELTYPE ("סוג בחירת דייר")
 *   - get/add/update_tenant_selections → ZCOC_TENANTSELECTION_SUBFORM (rows)
 *   - get_selection_statuses            → ZCOC_TENANTSELSTAT (statuses + flags)
 *   - get_tenant_sup_selection          → ZCOC_SUPCHOOSE (suppliers) [route TBD]
 *
 * Actions are thin pass-throughs: callers build the request body from
 * tenantsStore.selectedSiteSpace, e.g.
 *   { CUST, MAINENV, SPACENAME }                      for reads
 *   { CUST, MAINENV, SPACENAME, data: {...} }         for add/update
 */
const TenantsChooseStore = types
  .model("TenantsChooseStore", {
    tenantSpace: types.frozen(),
    selections: types.frozen(),
    changes: types.frozen(),
    selectionTypes: types.frozen(),
    apartmentPlans: types.frozen(),
    selectionStatuses: types.frozen(),
    activeChangeProfile: types.frozen(),
    suppliers: types.frozen(),
    models: types.frozen(),
    // dummy flag: has the tenant signed the changes document? false → show the
    // Lobby sign-prompt card + disable the "שינויי דייר" menu item.
    tenantChangeSignDoc: types.frozen(),
    // is the read-and-sign modal open? (hosted once, opened from card/popover)
    signModalOpen: types.optional(types.boolean, false),
    error: types.frozen(),
  })
  .actions((self) => {
    function setError(error, response) {
      self.error = cleanFormError(response, error);
    }

    const setTenantSpace = (value) => {
      self.tenantSpace = value;
    };
    const setSelections = (value) => {
      self.selections = value;
    };
    const setChanges = (value) => {
      self.changes = value;
    };
    const setSelectionTypes = (value) => {
      self.selectionTypes = value;
    };
    const setApartmentPlans = (value) => {
      self.apartmentPlans = value;
    };
    const setSelectionStatuses = (value) => {
      self.selectionStatuses = value;
    };
    const setActiveChangeProfile = (value) => {
      self.activeChangeProfile = value;
    };
    const setSuppliers = (value) => {
      self.suppliers = value;
    };
    const setModels = (value) => {
      self.models = value;
    };
    const setTenantChangeSignDoc = (value) => {
      self.tenantChangeSignDoc = value;
    };
    const openSignModal = () => {
      self.signModalOpen = true;
    };
    const closeSignModal = () => {
      self.signModalOpen = false;
    };

    // ---- Selections (table rows) -----------------------------------------
    const getSelections = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.get_selections();
      if (response?.status === 200 && response?.data) {
        self.setSelections(response.data);
        return response.data;
      }
      self.setError("error:error_failed_get_tenant_selections_req", response);
    };

    // ---- Changes (ZCOC_TENENTCHANGE) -------------------------------------
    const getChanges = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.get_changes();
      if (response?.status === 200 && response?.data) {
        self.setChanges(response.data);
        return response.data;
      }
      self.setError("error:error_failed_get_tenant_changes_req", response);
    };

    const addChange = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.add_change();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_add_tenant_change_req", response);
    };

    const updateChange = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.update_change();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_update_tenant_change_req", response);
    };

    const addSelection = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.add_selection();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_add_tenant_selection_req", response);
    };

    const updateSelection = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.update_selection();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_update_tenant_selection_req", response);
    };

    // ---- Selection types ("סוג בחירת דייר") -------------------------------
    const getSelectionTypes = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_selection_types();
      if (response?.status === 200 && response?.data) {
        self.setSelectionTypes(response.data);
        return response.data;
      }
      self.setError("error:error_failed_get_tenant_selection_types_req", response);
    };

    // ---- Tenant space (source of SITE / BUILDINGNUM / SPACETYPECODE) --------
    const getTenantSpace = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_tenant_space();
      if (response?.status === 200) {
        self.setTenantSpace(response.data || null);
        return response.data;
      }
      self.setError("error:error_failed_get_tenant_case_req", response);
    };

    // Save the signed price-quote PDF (add_signed_edqout):
    //   { CUST, MAINENV, SPACENAME, data: { EXTFILENAME: signedBase64 } }
    const addSignedEdqout = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.add_signed_edqout();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_add_signed_edqout_req", response);
    };

    // Read the signed price-quote PDF (signed_edqout).
    const getSignedEdqout = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.signed_edqout();
      if (response?.status === 200) {
        return response.data?.value || response.data;
      }
      self.setError("error:error_failed_get_signed_edqout_req", response);
    };

    // The doc the tenant must read + sign (tenantchange_signdoc). Its first row's
    // EXTFILENAME drives isChangeDocSigned / changeSignDocPdf.
    const getTenantChangeSignDoc = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.tenantchange_signdoc();
      if (response?.status === 200) {
        self.setTenantChangeSignDoc(response.data);
        return response.data;
      }
      self.setError("error:error_failed_get_tenantchange_signdoc_req", response);
    };

    // Save the signed changes document (add_tenantchange_signdoc):
    //   { CUST, MAINENV, SPACENAME, EXTFILENAME: signedBase64 }
    const addTenantChangeSignDoc = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.add_tenantchange_signdoc();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_add_tenantchange_signdoc_req", response);
    };

    // ---- Apartment plans (Plans tab + New Plan form) ----------------------
    const getApartmentPlans = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_apartment_plans();
      if (response?.status === 200 && response?.data) {
        self.setApartmentPlans(response.data);
        return response.data;
      }
      self.setError("error:error_failed_get_tenant_apartment_plans_req", response);
    };

    const addApartmentPlan = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.add_apartment_plan();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_add_tenant_apartment_plan_req", response);
    };

    const updateApartmentPlan = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.update_apartment_plan();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_update_tenant_apartment_plan_req", response);
    };

    // The plan status flagged SENTBYTENANT = "Y" (already filtered by the backend
    // to that row + STATDES) — set on a plan the tenant just submitted.
    const getApartmentPlanStatus = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_apartment_plan_status();
      if (response?.status === 200) {
        return response.data?.value || response.data;
      }
      self.setError("error:error_failed_get_apartment_plan_status_req", response);
    };

    // ---- Selection statuses (ZCOC_TENANTSELSTAT, all rows + their flags) ----
    const getSelectionStatuses = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_selection_statuses();
      if (response?.status === 200 && response?.data) {
        const value = response.data?.value || response.data;
        self.setSelectionStatuses(value);
        return value;
      }
      self.setError("error:error_failed_get_selection_statuses_req", response);
    };

    // ---- Active tenant-change profile (ZCOC_TENENTCHNGCPROF, ACTIVEFLAG="Y") --
    const getActiveChangeProfile = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_active_change_profile();
      if (response?.status === 200) {
        const value = response.data?.value || response.data;
        self.setActiveChangeProfile(value);
        return value;
      }
      self.setError("error:error_failed_get_active_change_profile_req", response);
    };

    // The CPROF status flagged SIGNBYTENANT = "Y" (CPROFSTATS screen).
    const getCprofSignedStatus = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_cprof_signed_status();
      if (response?.status === 200) {
        return response.data?.value || response.data;
      }
      self.setError("error:error_failed_get_cprof_signed_status_req", response);
    };

    // Patch a change profile (ZCOC_TENENTCHNGCPROF) by CPROFNUM.
    const updateChangeProfile = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.update_change_profile();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_update_change_profile_req", response);
    };

    // ---- Suppliers ("שם ספק נבחר") — backend route pending ----------------
    const getSupplierSelection = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_supplier_selection();
      if (response?.status === 200 && response?.data) {
        const value = response.data?.value || response.data;
        self.setSuppliers(value);
        return value;
      }
      self.setError("error:error_failed_get_tenant_sup_selection_req", response);
    };

    // ---- Models ("תאור דגם", standard) — building → space-type → detail → models,
    // filtered to the selected supplier.
    const getModels = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.get_models();
      if (response?.status === 200 && response?.data) {
        const value = response.data?.value || response.data;
        self.setModels(value);
        return value;
      }
      self.setError("error:error_failed_get_tenant_models_req", response);
    };

    return {
      setError,
      setTenantSpace,
      getTenantSpace,
      addSignedEdqout,
      getSignedEdqout,
      getTenantChangeSignDoc,
      addTenantChangeSignDoc,
      setSelections,
      setChanges,
      getChanges,
      addChange,
      updateChange,
      setSelectionTypes,
      setApartmentPlans,
      setSelectionStatuses,
      setActiveChangeProfile,
      setSuppliers,
      setModels,
      getModels,
      setTenantChangeSignDoc,
      openSignModal,
      closeSignModal,
      getSelections,
      addSelection,
      updateSelection,
      getSelectionTypes,
      getApartmentPlans,
      addApartmentPlan,
      updateApartmentPlan,
      getApartmentPlanStatus,
      getSelectionStatuses,
      getActiveChangeProfile,
      getCprofSignedStatus,
      updateChangeProfile,
      getSupplierSelection,
    };
  })
  .views((self) => ({
    get isError() {
      return !!self.error;
    },
    // The doc to sign: EXTFILENAME of the first still-unsigned row.
    get changeSignDocPdf() {
      return (
        signDocRows(self.tenantChangeSignDoc).find(isUnsignedRow)?.EXTFILENAME ??
        null
      );
    },
    // "signed" (→ changes button enabled + Lobby card/popover hidden) when no
    // row still needs signing. A row with EXTFILENAME + PROVIDED="Y" doesn't
    // block; only a doc that exists and isn't provided does.
    get isChangeDocSigned() {
      return !signDocRows(self.tenantChangeSignDoc).some(isUnsignedRow);
    },
  }));

const tenantsChooseStore = TenantsChooseStore.create({
  tenantSpace: null,
  selections: [],
  changes: [],
  selectionTypes: [],
  apartmentPlans: [],
  selectionStatuses: [],
  activeChangeProfile: null,
  suppliers: [],
  models: [],
  tenantChangeSignDoc: null,
  signModalOpen: false,
  error: "",
});

export default tenantsChooseStore;
